<script lang="ts" setup>
import { computed, ref } from 'vue'
import { storeToRefs } from 'pinia'

import { useSessionStore } from '@/stores/session'
import { useTeslaCamStore } from '@/stores/teslacam'
import { TESLA_CATEGORY_LABELS, getTeslaEventInfo } from '@/utils/vendors/tesla'
import {
  filesFromDirectoryHandle,
  pickDirectory,
  supportsDirectoryPicker
} from '@/utils/vendors/tesla-folder'
import type { DriveProblem, NavigatorRow } from '@/utils/vendors/tesla-navigator'
import { formatDate, formatNumber, formatTime } from '@/utils/format'
import TeslaEventThumb from '@/components/teslacam/TeslaEventThumb.vue'

const sessionStore = useSessionStore()
const teslaStore = useTeslaCamStore()
const { isLoggedIn, isLoggingIn } = storeToRefs(sessionStore)
const { folderName, isParsing, problem, events, rows, countsByCategory } = storeToRefs(teslaStore)

const fileInputRef = ref<HTMLInputElement | null>(null)

const categoryChips = computed(() =>
  Object.entries(TESLA_CATEGORY_LABELS)
    .filter(([key]) => countsByCategory.value[key])
    .map(([key, label]) => ({ key, label, count: countsByCategory.value[key] }))
)

const PROBLEM_COPY: Record<DriveProblem, { title: string; body: string }> = {
  encrypted: {
    title: 'Your drive looks encrypted',
    body: 'Turn off USB encryption in your Tesla, then plug the drive in again. We cannot read encrypted footage.'
  },
  empty: {
    title: 'No clips found',
    body: 'That folder has no TeslaCam clips in it. Pick the TeslaCam folder from the root of your USB drive.'
  },
  'not-teslacam': {
    title: 'That does not look like a TeslaCam folder',
    body: 'Pick the folder named TeslaCam on your USB drive. It holds SavedClips, SentryClips and RecentClips.'
  },
  unreadable: {
    title: 'We could not read that folder',
    body: 'Check the drive is still plugged in and try again. If it keeps failing, it may be encrypted or damaged.'
  }
}

async function chooseFolder() {
  if (isParsing.value) return
  if (!supportsDirectoryPicker()) {
    fileInputRef.value?.click()
    return
  }
  try {
    const handle = await pickDirectory()
    await teslaStore.loadFiles(await filesFromDirectoryHandle(handle), handle.name)
  } catch (err) {
    if ((err as DOMException)?.name === 'AbortError') return
    console.error('TeslaCam folder pick failed', err)
    teslaStore.clear()
    problem.value = 'unreadable'
  }
}

async function onInputFiles(e: Event) {
  const input = e.target as HTMLInputElement
  const files = Array.from(input.files ?? [])
  input.value = '' // Lets the same folder be picked again.
  if (files.length === 0) return
  const rel = (files[0] as File & { webkitRelativePath?: string }).webkitRelativePath
  await teslaStore.loadFiles(files, rel?.split('/')[0] || 'Selected folder')
}

function placeOf(row: Extract<NavigatorRow, { kind: 'event' }>): string {
  return getTeslaEventInfo(row.event).place || 'Location unknown'
}

function categoryOf(row: Extract<NavigatorRow, { kind: 'event' }>): string {
  const { category } = getTeslaEventInfo(row.event)
  return category ? TESLA_CATEGORY_LABELS[category] ?? category : ''
}
</script>

<template>
  <q-page padding>
    <div class="teslacam-page">
      <h1 class="text-h5 q-mt-none q-mb-sm text-blue-grey-9">TeslaCam</h1>

      <div v-if="!isLoggedIn" class="text-body1 text-blue-grey-8" data-testid="teslacam-signin">
        <p>Sign in to browse the footage on your Tesla USB drive.</p>
        <q-btn
          unelevated
          color="blue-8"
          label="Sign in"
          :loading="isLoggingIn"
          @click="sessionStore.loginWithRedirect()"
        />
      </div>

      <template v-else>
        <p class="text-body2 text-blue-grey-8">
          See every event on your Tesla USB drive in one place. Nothing is uploaded, and nothing is
          deleted from the drive.
        </p>

        <input
          ref="fileInputRef"
          type="file"
          class="hidden"
          webkitdirectory
          multiple
          data-testid="teslacam-input"
          @change="onInputFiles"
        />

        <div class="row items-center q-gutter-sm q-mb-md">
          <q-btn
            unelevated
            color="blue-8"
            icon="mdi-folder-open"
            :label="folderName ? 'Choose a different folder' : 'Choose TeslaCam folder'"
            :disable="isParsing"
            @click="chooseFolder"
          />
          <span v-if="folderName" class="text-body2 text-blue-grey-9 text-weight-medium">
            <q-icon name="mdi-folder-check" color="blue-8" /> {{ folderName }}
          </span>
        </div>

        <div v-if="!folderName" class="text-caption text-blue-grey-7">
          Pick the <code>TeslaCam</code> folder at the root of your USB drive.
          <q-expansion-item dense label="Can't open the drive?" class="q-mt-sm">
            <div class="q-pa-sm text-body2">
              Plug the drive into your computer, or into your phone or tablet with a USB adapter. If
              your browser cannot pick a folder, try Chrome or Edge on a computer. A drive formatted
              by the car is exFAT or FAT32, which most devices read. If your Tesla encrypts the
              drive, turn that off in the car first.
            </div>
          </q-expansion-item>
        </div>

        <div
          v-if="isParsing"
          class="row items-center no-wrap q-gutter-x-sm"
          data-testid="teslacam-parsing"
        >
          <q-spinner-dots color="blue-8" size="1.6em" />
          <span class="text-body2 text-blue-grey-8">Reading folder…</span>
        </div>

        <q-banner
          v-else-if="problem"
          rounded
          class="bg-orange-1 text-blue-grey-9"
          data-testid="teslacam-problem"
          :data-problem="problem"
        >
          <template #avatar><q-icon name="mdi-alert-circle-outline" color="orange-9" /></template>
          <div class="text-weight-bold">{{ PROBLEM_COPY[problem].title }}</div>
          <div>{{ PROBLEM_COPY[problem].body }}</div>
        </q-banner>

        <template v-else-if="events.length > 0">
          <div class="row items-center q-gutter-sm q-mb-sm" data-testid="teslacam-summary">
            <span class="text-subtitle1 text-weight-medium text-blue-grey-9">
              {{ formatNumber(events.length) }} event{{ events.length === 1 ? '' : 's' }}
            </span>
            <q-chip v-for="chip in categoryChips" :key="chip.key" dense color="blue-grey-2">
              {{ chip.label }} {{ formatNumber(chip.count) }}
            </q-chip>
          </div>

          <q-virtual-scroll
            class="event-list"
            :items="rows"
            :virtual-scroll-item-size="84"
            :virtual-scroll-slice-size="20"
            data-testid="teslacam-list"
          >
            <template #default="{ item: row }">
              <div
                v-if="row.kind === 'day'"
                :key="row.key"
                class="day-header row items-center text-subtitle2 text-blue-grey-8 bg-blue-grey-1 q-px-sm"
                data-testid="teslacam-day"
              >
                {{ row.date ? formatDate(row.date) : 'Unknown date' }}
                <span class="text-caption text-blue-grey-6 q-ml-sm">({{ row.count }})</span>
              </div>
              <div
                v-else
                :key="row.key"
                class="event-row row items-center no-wrap q-px-sm q-gutter-x-md"
                data-testid="teslacam-event"
              >
                <tesla-event-thumb :event="row.event" />
                <div class="col ellipsis-container">
                  <div class="text-body2 text-weight-medium text-blue-grey-9">
                    {{ row.event.dateTime ? formatTime(row.event.dateTime) : 'Unknown time' }}
                  </div>
                  <div class="text-body2 text-blue-grey-8 ellipsis">{{ placeOf(row) }}</div>
                  <div class="text-caption text-blue-grey-7 ellipsis">
                    {{ row.event.displayLabel }}
                    <q-badge color="blue-grey-6" class="q-ml-xs">{{ categoryOf(row) }}</q-badge>
                  </div>
                </div>
              </div>
            </template>
          </q-virtual-scroll>
        </template>
      </template>
    </div>
  </q-page>
</template>

<style scoped>
.teslacam-page {
  max-width: 900px;
  margin: 0 auto;
}

.hidden {
  display: none;
}

.event-list {
  height: 65vh;
  min-height: 320px;
  border: 1px solid var(--q-blue-grey-2, #cfd8dc);
  border-radius: 4px;
}

.day-header {
  height: 40px;
}

.event-row {
  height: 84px;
}

.ellipsis-container {
  min-width: 0;
}
</style>
